import React, { useEffect, useState } from "react";
import { Link, useNavigate, Routes, Route } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Users, ShoppingBag, Package, AlertTriangle, TrendingUp, Euro, Shield, Search, Trash2, Ban, Check, ArrowLeft, Handshake, Video, Radio, Copy, Flag } from "lucide-react";

const STATUS_LABEL = {
  escrow_paid: "Séquestré",
  shipped: "Expédié",
  released: "Libéré",
  disputed: "Litige",
  refunded: "Remboursé",
};

export default function Admin() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [mrStatus, setMrStatus] = useState(null);
  const [broadcasting, setBroadcasting] = useState(false);

  const broadcastStudioIA = async () => {
    if (!window.confirm("Envoyer l'annonce Studio Photo IA (notification + push) à TOUS les vendeurs ? (envoi unique)")) return;
    setBroadcasting(true);
    try {
      const { data } = await api.post("/admin/broadcast/studio-ia");
      toast.success(`Annonce envoyée à ${data.sent} vendeur(s) 📣`);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setBroadcasting(false); }
  };

  useEffect(() => {
    if (!loading && (!user || user.role !== "admin")) navigate("/login");
  }, [user, loading, navigate]);

  useEffect(() => {
    if (user?.role === "admin") api.get("/admin/mr-balance-status").then(r => setMrStatus(r.data)).catch(() => {});
  }, [user]);

  if (!user || user.role !== "admin") return <div className="p-20 text-center">Accès admin requis</div>;

  return (
    <div className="bg-black text-white min-h-screen has-bottom-nav" data-testid="admin-page">
      <div className="max-w-[1400px] mx-auto px-4 lg:px-8 py-6 lg:py-10">
        {mrStatus?.exhausted && (
          <div className="mb-6 p-4 bg-[#E02020] border-2 border-white flex flex-wrap items-center gap-3" data-testid="mr-balance-banner">
            <AlertTriangle size={22} strokeWidth={2.5} className="shrink-0"/>
            <div className="flex-1 min-w-[220px]">
              <p className="font-black uppercase tracking-wide text-sm">Solde Mondial Relay épuisé</p>
              <p className="text-xs text-white/90">Les bordereaux d'envoi sont bloqués — les vendeurs ne peuvent plus expédier. Recharge le compte prépayé pour débloquer.</p>
            </div>
            <a href="https://www.mondialrelay.fr/connexion/" target="_blank" rel="noreferrer" className="btn bg-white text-black border-white shrink-0" data-testid="mr-recharge-link">Recharger →</a>
          </div>
        )}
        <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-[color:var(--color-mint)] flex items-center justify-center">
              <Shield size={22} strokeWidth={2.5} className="text-black"/>
            </div>
            <div>
              <p className="label text-[color:var(--color-mint)]">Fripri · Admin</p>
              <h1 className="text-3xl lg:text-4xl font-black tracking-tighter">Console</h1>
            </div>
          </div>
          <Link to="/" className="btn bg-white text-black border-white"><ArrowLeft size={14}/> Retour app</Link>
        </div>

        <div className="mb-6 p-4 border-2 border-white/30 flex flex-wrap items-center gap-3" data-testid="studio-ia-broadcast-card">
          <div className="flex-1 min-w-[220px]">
            <p className="font-black text-sm">📣 Annonce Studio Photo IA</p>
            <p className="text-xs text-white/60">Préviens tous les vendeurs du nouveau rendu mannequin + conseils photo (notification + push, envoi unique).</p>
          </div>
          <button onClick={broadcastStudioIA} disabled={broadcasting} className="btn bg-[color:var(--color-mint)] text-black border-[color:var(--color-mint)] shrink-0 disabled:opacity-50" data-testid="broadcast-studio-ia-btn">
            {broadcasting ? "Envoi…" : "Envoyer aux vendeurs"}
          </button>
        </div>
        <div className="flex gap-2 mb-6 overflow-x-auto no-scrollbar">
          <TabLink to="/admin">Dashboard</TabLink>
          <TabLink to="/admin/users">Utilisateurs</TabLink>
          <TabLink to="/admin/products">Annonces</TabLink>
          <TabLink to="/admin/orders">Commandes</TabLink>
          <TabLink to="/admin/labels">Bordereaux</TabLink>
          <TabLink to="/admin/disputes">Litiges</TabLink>
          <TabLink to="/admin/agents">Agents</TabLink>
          <TabLink to="/admin/live">Brocante Live</TabLink>
          <TabLink to="/admin/reports">Signalements</TabLink>
          <TabLink to="/admin/pro">Pro</TabLink>
        </div>

        <Routes>
          <Route index element={<AdminStats/>} />
          <Route path="users" element={<AdminUsers/>} />
          <Route path="products" element={<AdminProducts/>} />
          <Route path="orders" element={<AdminOrders/>} />
          <Route path="labels" element={<AdminLabels/>} />
          <Route path="disputes" element={<AdminDisputes/>} />
          <Route path="agents" element={<AdminAgents/>} />
          <Route path="live" element={<AdminLive/>} />
          <Route path="reports" element={<AdminReports/>} />
          <Route path="pro" element={<AdminPro/>} />
        </Routes>
      </div>
    </div>
  );
}

function TabLink({ to, children }) {
  const seg = to.split("/").pop();
  const slug = !seg || seg === "admin" ? "dashboard" : seg;
  return (
    <Link to={to} className="btn bg-white/10 text-white border-white/20 hover:bg-[color:var(--color-mint)] hover:text-black hover:border-[color:var(--color-mint)] btn-sm shrink-0" data-testid={`admin-tab-${slug}`}>
      {children}
    </Link>
  );
}

const ACTIVITY_LABELS = { vide_dressing: "Vide-dressing régulier", friperie: "Friperie / revendeur", createur: "Créateur / upcycling", autre: "Autre" };
const VOLUME_LABELS = { moins_500: "< 500 €/mois", "500_2000": "500-2 000 €/mois", plus_2000: "> 2 000 €/mois" };

function AdminPro() {
  const [apps, setApps] = useState([]);
  const [appStats, setAppStats] = useState(null);
  const refresh = () => api.get("/admin/pro-applications").then(r => setApps(r.data));
  useEffect(() => {
    refresh();
    api.get("/admin/app-stats").then(r => setAppStats(r.data)).catch(() => {});
  }, []);

  const decide = async (a, action) => {
    let note;
    if (action === "reject") note = window.prompt("Motif du refus (envoyé au vendeur) :") || undefined;
    try {
      await api.post(`/admin/pro-applications/${a.application_id}/decide`, { action, note });
      toast.success(action === "approve" ? "Vendeur passé PRO ✓" : "Demande refusée");
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const revoke = async (a) => {
    if (!window.confirm(`Retirer le statut PRO de ${a.user_name} ?`)) return;
    try {
      await api.post(`/admin/users/${a.user_id}/revoke-pro`);
      toast.success("Statut PRO retiré");
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  return (
    <div data-testid="admin-pro-section">
      {appStats && (
        <div className="mb-8" data-testid="admin-app-stats">
          <h2 className="text-xl font-black mb-3">Applications mobiles 📱</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="border-2 border-white/20 bg-white/5 p-3"><p className="text-2xl font-black">{appStats.total_devices}</p><p className="text-xs text-white/60">Appareils app détectés</p></div>
            <div className="border-2 border-white/20 bg-white/5 p-3"><p className="text-2xl font-black">{appStats.ios}</p><p className="text-xs text-white/60">iOS</p></div>
            <div className="border-2 border-white/20 bg-white/5 p-3"><p className="text-2xl font-black">{appStats.android}</p><p className="text-xs text-white/60">Android</p></div>
            <div className="border-2 border-white/20 bg-white/5 p-3"><p className="text-2xl font-black">{appStats.active_7d}</p><p className="text-xs text-white/60">Actifs 7 jours</p></div>
          </div>
          <p className="text-xs text-white/50 mt-2">{appStats.ios_notifications} appareil(s) iOS avec notifications activées · Chiffres officiels : <a href="https://appstoreconnect.apple.com/analytics" target="_blank" rel="noopener noreferrer" className="underline">App Store Connect</a> · <a href="https://play.google.com/console" target="_blank" rel="noopener noreferrer" className="underline">Play Console</a></p>
        </div>
      )}
      <h2 className="text-xl font-black mb-4">Demandes Fripri Pro ({apps.filter(a => a.status === "pending").length} en attente)</h2>
      {apps.length === 0 ? <p className="text-white/60">Aucune demande pour l'instant.</p> : (
        <div className="space-y-3">
          {apps.map(a => (
            <div key={a.application_id} className="border-2 border-white/20 bg-white/5 p-4" data-testid={`pro-app-${a.application_id}`}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0">
                  <p className="font-black">{a.user_name} <span className="text-white/50 font-normal text-sm">· {a.user_email}</span></p>
                  <p className="text-sm text-white/70 mt-1">
                    {ACTIVITY_LABELS[a.activity] || a.activity} · {VOLUME_LABELS[a.estimated_volume] || a.estimated_volume}
                    {a.company_name && <> · Entreprise : {a.company_name}</>}
                    {a.phone && <> · 📞 {a.phone}</>}
                  </p>
                  {a.message && <p className="text-sm text-white/60 mt-2 italic">« {a.message} »</p>}
                  <p className="text-xs text-white/40 mt-2">{new Date(a.created_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" })}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {a.status === "pending" ? (
                    <>
                      <button onClick={() => decide(a, "approve")} className="btn btn-sm bg-[color:var(--color-mint)] text-black border-[color:var(--color-mint)]" data-testid={`approve-pro-${a.application_id}`}>
                        <Check size={14}/> Valider PRO
                      </button>
                      <button onClick={() => decide(a, "reject")} className="btn btn-sm bg-[color:var(--color-coral)] text-white border-[color:var(--color-coral)]" data-testid={`reject-pro-${a.application_id}`}>
                        <Ban size={14}/> Refuser
                      </button>
                    </>
                  ) : (
                    <>
                      <span className={`pill ${a.status === "approved" ? "pill-mint" : "pill-coral"}`}>{a.status === "approved" ? "Validée ✓" : a.status === "revoked" ? "PRO retiré" : "Refusée"}</span>
                      {a.status === "approved" && (
                        <button onClick={() => revoke(a)} className="btn btn-sm border-2 border-[color:var(--color-coral)] text-[color:var(--color-coral)] hover:bg-[color:var(--color-coral)] hover:text-white" data-testid={`revoke-pro-${a.application_id}`}>
                          Retirer PRO
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AdminStats() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/admin/stats").then(r => setStats(r.data)); }, []);
  if (!stats) return <div className="text-white/60">Chargement…</div>;
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
      <StatCard icon={<Users size={20}/>} label="Utilisateurs" value={stats.users_count} testid="stat-users"/>
      <StatCard icon={<Package size={20}/>} label="Annonces actives" value={stats.active_products} testid="stat-products"/>
      <StatCard icon={<ShoppingBag size={20}/>} label="Commandes" value={stats.orders_count} testid="stat-orders"/>
      <StatCard icon={<AlertTriangle size={20}/>} label="Litiges" value={stats.disputes_count} color="bg-[color:var(--color-coral)] text-white" testid="stat-disputes"/>
      <StatCard icon={<TrendingUp size={20}/>} label="Volume" value={`${stats.total_volume.toFixed(0)}€`} color="bg-[color:var(--color-mint)] text-black" testid="stat-volume"/>
      <StatCard icon={<Euro size={20}/>} label="Commission" value={`${stats.total_commission.toFixed(0)}€`} color="bg-[color:var(--color-yellow)] text-black" testid="stat-commission"/>
    </div>
  );
}

function StatCard({ icon, label, value, color = "bg-white text-black", testid }) {
  return (
    <div className={`p-4 lg:p-5 border-2 border-white ${color}`} data-testid={testid}>
      <div className="flex items-center justify-between mb-3">{icon}<span className="label">{label}</span></div>
      <div className="display-num text-3xl lg:text-4xl font-black">{value}</div>
    </div>
  );
}

function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const refresh = () => api.get("/admin/users", { params: q ? { q } : {} }).then(r => setUsers(r.data));
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [q]);

  const toggleBlock = async (u) => {
    try {
      await api.patch(`/admin/users/${u.user_id}`, { blocked: !u.blocked });
      toast.success(u.blocked ? "Débloqué" : "Bloqué");
      refresh();
    } catch { toast.error("Erreur"); }
  };
  const toggleLive = async (u) => {
    try {
      await api.patch(`/admin/users/${u.user_id}`, { live_access: !u.live_access });
      toast.success(u.live_access ? "Accès Live retiré" : "Accès Brocante Live attribué ✓");
      refresh();
    } catch { toast.error("Erreur"); }
  };
  const del = async (u) => {
    if (!window.confirm(`Supprimer ${u.email} ?`)) return;
    try { await api.delete(`/admin/users/${u.user_id}`); toast.success("Supprimé"); refresh(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-4 bg-white text-black px-3 border-2 border-white">
        <Search size={16}/>
        <input value={q} onChange={e=>setQ(e.target.value)} placeholder="Email ou nom…" className="bg-transparent py-2.5 outline-none flex-1 text-sm" data-testid="admin-users-search"/>
      </div>
      <div className="space-y-2">
        {users.map(u => (
          <div key={u.user_id} className="bg-white text-black p-3 flex items-center gap-3 border-2 border-white" data-testid={`admin-user-${u.user_id}`}>
            <div className="w-10 h-10 bg-black text-white flex items-center justify-center font-bold">{u.name?.[0]?.toUpperCase() || "?"}</div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <p className="font-bold truncate">{u.name}</p>
                {u.role === "admin" && <span className="pill pill-mint">Admin</span>}
                {u.role === "agent" && <span className="pill" style={{background:"var(--color-yellow)"}}>Agent {u.agent_department || ""}</span>}
                {u.live_access && <span className="pill pill-coral">Live</span>}
                {u.blocked && <span className="pill pill-coral">Bloqué</span>}
              </div>
              <p className="text-xs text-[color:var(--color-text-muted)] truncate">{u.email}</p>
              <p className="text-xs text-[color:var(--color-text-muted)] mt-0.5">★ {u.rating || 0} · {u.review_count || 0} avis</p>
            </div>
            <div className="flex flex-col gap-2">
              {u.role !== "admin" && (
                <>
                  <button onClick={()=>toggleLive(u)} className={`w-9 h-9 border-2 border-black flex items-center justify-center ${u.live_access ? "bg-[color:var(--color-coral)] text-white" : "hover:bg-[color:var(--color-mint)]"}`} data-testid={`live-access-${u.user_id}`} title={u.live_access ? "Retirer l'accès Brocante Live" : "Attribuer l'accès Brocante Live"}>
                    <Video size={14}/>
                  </button>
                  <button onClick={()=>toggleBlock(u)} className="w-9 h-9 border-2 border-black flex items-center justify-center hover:bg-[color:var(--color-yellow)]" data-testid={`block-${u.user_id}`} title={u.blocked ? "Débloquer" : "Bloquer"}>
                    {u.blocked ? <Check size={14}/> : <Ban size={14}/>}
                  </button>
                  <button onClick={()=>del(u)} className="w-9 h-9 border-2 border-black flex items-center justify-center hover:bg-[color:var(--color-coral)] hover:text-white" data-testid={`del-${u.user_id}`}><Trash2 size={14}/></button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function AdminProducts() {
  const [products, setProducts] = useState([]);
  useEffect(() => { api.get("/admin/products").then(r => setProducts(r.data)); }, []);
  const del = async (p) => {
    if (!window.confirm(`Supprimer "${p.title}" ?`)) return;
    try { await api.delete(`/admin/products/${p.product_id}`); setProducts(products.filter(x => x.product_id !== p.product_id)); toast.success("Supprimé"); }
    catch { toast.error("Erreur"); }
  };
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
      {products.map(p => (
        <div key={p.product_id} className="bg-white text-black p-3 border-2 border-white flex gap-3" data-testid={`admin-product-${p.product_id}`}>
          <div className="w-20 h-20 shrink-0 bg-[color:var(--color-surface)] overflow-hidden">
            {p.images?.[0] && <img src={fileUrl(p.images[0])} className="w-full h-full object-cover"/>}
          </div>
          <div className="flex-1 min-w-0">
            <p className="label">{p.brand}</p>
            <p className="font-bold truncate">{p.title}</p>
            <p className="font-black mt-1" style={{color:"var(--color-coral)"}}>{p.price?.toFixed(0)}€</p>
            <span className="pill mt-1 inline-block">{p.status}</span>
          </div>
          <button onClick={()=>del(p)} className="w-9 h-9 border-2 border-black flex items-center justify-center hover:bg-[color:var(--color-coral)] hover:text-white self-start" data-testid={`del-prod-${p.product_id}`}><Trash2 size={14}/></button>
        </div>
      ))}
    </div>
  );
}

const PKG_LABEL = { small: "Petit", medium: "Moyen", large: "Grand" };

function AdminLabels() {
  const [labels, setLabels] = useState([]);
  useEffect(() => {
    api.get("/admin/labels").then(r => setLabels(r.data)).catch(() => {});
  }, []);
  return (
    <div className="space-y-2" data-testid="admin-labels">
      {labels.length === 0 && <p className="text-white/60">Aucun bordereau généré pour le moment.</p>}
      {labels.map(o => (
        <div key={o.order_id} className="bg-white text-black p-3 border-2 border-white flex flex-wrap items-center justify-between gap-3" data-testid={`admin-label-${o.order_id}`}>
          <div className="min-w-0 flex-1">
            <p className="label">#{o.order_id.slice(-8).toUpperCase()} · {new Date(o.created_at).toLocaleDateString("fr-FR")} · Colis {PKG_LABEL[o.package_size] || "Petit"}</p>
            <p className="font-bold truncate">{(o.seller_names || []).join(", ")} → {o.buyer_name}</p>
            <p className="text-xs font-mono">Suivi : {o.tracking_number}</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className={`pill ${o.status === "disputed" ? "pill-coral" : o.status === "released" ? "pill-mint" : ""}`}>{STATUS_LABEL[o.status] || o.status}</span>
            {o.label_real ? (
              <a href={o.label_url} target="_blank" rel="noreferrer" className="btn btn-primary text-xs px-3 py-2" data-testid={`label-pdf-${o.order_id}`}>PDF ↗</a>
            ) : (
              <span className="pill pill-coral text-xs">Étiquette invalide</span>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function AdminOrders({ statusFilter }) {
  const [orders, setOrders] = useState([]);
  useEffect(() => {
    const params = statusFilter ? { status: statusFilter } : {};
    api.get("/admin/orders", { params }).then(r => setOrders(r.data));
  }, [statusFilter]);
  return (
    <div className="space-y-2">
      {orders.length === 0 && <p className="text-white/60">Aucune commande{statusFilter ? " " + statusFilter : ""}.</p>}
      {orders.map(o => (
        <div key={o.order_id} className="bg-white text-black p-3 border-2 border-white flex items-center justify-between gap-3" data-testid={`admin-order-${o.order_id}`}>
          <div className="min-w-0">
            <p className="label">#{o.order_id.slice(-8).toUpperCase()} · {new Date(o.created_at).toLocaleDateString("fr-FR")}</p>
            <p className="font-bold">{o.product_ids?.length || 0} article(s) · Buyer {o.buyer_id.slice(-6)}</p>
            <p className="text-xs text-[color:var(--color-text-muted)]">Commission: {o.commission_amount?.toFixed(2)}€ · Payout: {o.seller_payout?.toFixed(2)}€</p>
          </div>
          <div className="text-right">
            <p className="font-black text-xl" style={{color:"var(--color-coral)"}}>{o.amount?.toFixed(0)}€</p>
            <span className={`pill ${o.status === 'disputed' ? 'pill-coral' : o.status === 'released' ? 'pill-mint' : ''}`}>{STATUS_LABEL[o.status] || o.status}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

function AdminDisputes() {
  const [disputes, setDisputes] = useState([]);
  const refresh = () => api.get("/admin/orders", { params: { status: "disputed" } }).then(r => setDisputes(r.data));
  useEffect(() => { refresh(); }, []);
  const resolve = async (orderId, action) => {
    if (!window.confirm(`${action === "release" ? "Libérer fonds au vendeur" : "Rembourser l'acheteur"} ?`)) return;
    try { await api.post(`/admin/orders/${orderId}/resolve`, { action }); toast.success("Résolu"); refresh(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  return (
    <div className="space-y-3">
      {disputes.length === 0 && <p className="text-white/60">Aucun litige en cours. ✓</p>}
      {disputes.map(o => (
        <div key={o.order_id} className="bg-white text-black p-4 border-2 border-[color:var(--color-coral)]" data-testid={`dispute-${o.order_id}`}>
          <div className="flex items-start justify-between mb-3 gap-3">
            <div className="min-w-0">
              <p className="label">#{o.order_id.slice(-8).toUpperCase()}</p>
              <p className="font-bold">{o.amount?.toFixed(2)}€ · {o.product_ids?.length} article(s)</p>
              <p className="text-xs text-[color:var(--color-text-muted)] mt-1">Litige ouvert le {new Date(o.disputed_at).toLocaleDateString("fr-FR")}</p>
            </div>
            <span className="pill pill-coral">Litige</span>
          </div>
          <div className="p-3 bg-[color:var(--color-surface)] mb-3 text-sm">
            <p className="label font-bold mb-1">Motif :</p>
            <p>{o.dispute_reason || "Aucun motif fourni"}</p>
          </div>
          <div className="flex gap-2">
            <button onClick={()=>resolve(o.order_id, "release")} className="btn btn-mint flex-1 btn-sm" data-testid={`release-${o.order_id}`}>Libérer au vendeur</button>
            <button onClick={()=>resolve(o.order_id, "refund")} className="btn btn-coral flex-1 btn-sm" data-testid={`refund-${o.order_id}`}>Rembourser l'acheteur</button>
          </div>
        </div>
      ))}
    </div>
  );
}

function AdminAgents() {
  const [agents, setAgents] = useState([]);
  const [applications, setApplications] = useState([]);
  const [form, setForm] = useState({ email: "", name: "", password: "", department: "" });
  const [saving, setSaving] = useState(false);
  const refresh = () => {
    api.get("/admin/agents").then(r => setAgents(r.data));
    api.get("/admin/agent-applications").then(r => setApplications(r.data)).catch(() => {});
  };
  useEffect(() => { refresh(); }, []);

  const review = async (a, action) => {
    if (!window.confirm(`${action === "approve" ? "Accepter" : "Refuser"} la candidature de ${a.name} (${a.department}) ?`)) return;
    try {
      const r = await api.patch(`/admin/agent-applications/${a.application_id}`, { action });
      toast.success(action === "approve" ? (r.data.auto_promoted ? "Acceptée — compte promu agent ✓" : "Acceptée — créez son compte agent ci-dessous avec son email") : "Candidature refusée");
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const r = await api.post("/admin/agents", form);
      toast.success(r.data.created ? "Agent créé ✓" : "Utilisateur promu agent ✓");
      setForm({ email: "", name: "", password: "", department: "" });
      refresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  const revoke = async (a) => {
    if (!window.confirm(`Révoquer l'agent ${a.name} (${a.email}) ?`)) return;
    try { await api.patch(`/admin/agents/${a.user_id}`, { revoke: true }); toast.success("Agent révoqué"); refresh(); }
    catch { toast.error("Erreur"); }
  };

  const copyCode = (code) => { navigator.clipboard.writeText(code); toast.success("Code copié ✓"); };

  return (
    <div className="space-y-6">
      {/* Candidatures en attente */}
      {applications.filter(a => a.status === "pending").length > 0 && (
        <div className="bg-[color:var(--color-yellow)] text-black p-4 border-2 border-white space-y-3" data-testid="applications-panel">
          <h3 className="font-black tracking-tight">📥 Candidatures en attente ({applications.filter(a => a.status === "pending").length})</h3>
          {applications.filter(a => a.status === "pending").map(a => (
            <div key={a.application_id} className="bg-white p-3 border-2 border-black" data-testid={`application-${a.application_id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <p className="font-black">{a.name} <span className="pill pill-mint ml-1">Dépt {a.department}</span></p>
                  <p className="text-xs text-[color:var(--color-text-muted)]">{a.email}{a.phone ? ` · ${a.phone}` : ""}{a.city ? ` · ${a.city}` : ""} · {new Date(a.created_at).toLocaleDateString("fr-FR")}</p>
                  <p className="text-sm mt-1 italic">« {a.motivation} »</p>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button onClick={()=>review(a, "approve")} className="btn btn-mint btn-sm" data-testid={`approve-${a.application_id}`}>Accepter</button>
                  <button onClick={()=>review(a, "reject")} className="btn btn-sm border-2 border-black hover:bg-[color:var(--color-coral)] hover:text-white" data-testid={`reject-${a.application_id}`}>Refuser</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <form onSubmit={create} className="bg-white text-black p-4 border-2 border-white space-y-3" data-testid="create-agent-form">
        <h3 className="font-black tracking-tight flex items-center gap-2"><Handshake size={18}/> Recruter un agent FRIPRI</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <input className="field" type="email" required placeholder="Email de l'agent" value={form.email} onChange={e=>setForm({...form, email:e.target.value})} data-testid="agent-email-input"/>
          <input className="field" placeholder="Nom (optionnel si compte existant)" value={form.name} onChange={e=>setForm({...form, name:e.target.value})} data-testid="agent-name-input"/>
          <input className="field" type="password" placeholder="Mot de passe (8 car. min, nouveau compte)" value={form.password} onChange={e=>setForm({...form, password:e.target.value})} data-testid="agent-password-input"/>
          <input className="field" required maxLength={3} placeholder="Département (75, 2A, 971…)" value={form.department} onChange={e=>setForm({...form, department:e.target.value})} data-testid="agent-dept-input"/>
        </div>
        <p className="text-xs text-[color:var(--color-text-muted)]">Si l'email correspond à un compte existant, il sera promu agent (pas besoin de mot de passe). L'agent touche 3% sur les ventes de ses filleuls.</p>
        <button type="submit" disabled={saving} className="btn btn-primary btn-sm" data-testid="agent-submit-btn">{saving ? "Création…" : "Créer l'agent →"}</button>
      </form>

      <div className="space-y-2" data-testid="agents-list">
        {agents.length === 0 && <p className="text-white/60">Aucun agent recruté pour l'instant.</p>}
        {agents.map(a => (
          <div key={a.user_id} className="bg-white text-black p-3 flex items-center gap-3 border-2 border-white flex-wrap" data-testid={`agent-row-${a.user_id}`}>
            <div className="w-10 h-10 bg-[color:var(--color-mint)] border-2 border-black flex items-center justify-center font-bold shrink-0">{a.agent_department || "?"}</div>
            <div className="flex-1 min-w-0">
              <p className="font-bold truncate">{a.name}</p>
              <p className="text-xs text-[color:var(--color-text-muted)] truncate">{a.email}</p>
              <button onClick={()=>copyCode(a.referral_code)} className="text-xs font-black inline-flex items-center gap-1 mt-0.5 hover:text-[color:var(--color-coral)]" data-testid={`copy-code-${a.user_id}`}>
                {a.referral_code} <Copy size={10}/>
              </button>
            </div>
            <div className="text-right text-xs shrink-0">
              <p className="font-black">{a.referred_count} filleul(s)</p>
              <p>Attente : <strong>{a.commission_pending?.toFixed(2)}€</strong></p>
              <p>Versé : <strong className="text-[color:var(--color-coral)]">{a.commission_paid?.toFixed(2)}€</strong></p>
            </div>
            <button onClick={()=>revoke(a)} className="w-9 h-9 border-2 border-black flex items-center justify-center hover:bg-[color:var(--color-coral)] hover:text-white shrink-0" data-testid={`revoke-${a.user_id}`} title="Révoquer"><Trash2 size={14}/></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function AdminLive() {
  const [sessions, setSessions] = useState([]);
  const refresh = () => api.get("/live/sessions").then(r => setSessions(r.data));
  useEffect(() => { refresh(); }, []);

  const endSession = async (s) => {
    if (!window.confirm(`Terminer la session "${s.title}" ?`)) return;
    try { await api.patch(`/live/sessions/${s.session_id}`, { action: "end" }); toast.success("Session terminée"); refresh(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const del = async (s) => {
    if (!window.confirm(`Supprimer la session "${s.title}" et tout son contenu ?`)) return;
    try { await api.delete(`/live/sessions/${s.session_id}`); toast.success("Supprimée"); refresh(); }
    catch { toast.error("Erreur"); }
  };

  return (
    <div className="space-y-4">
      <div className="bg-white text-black p-4 border-2 border-white flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-black tracking-tight flex items-center gap-2"><Radio size={18}/> Sessions Brocante Live</h3>
          <p className="text-xs text-[color:var(--color-text-muted)]">Créez des sessions depuis la page <strong>/brocante-live</strong> (avec assignation d'hôte). Attribuez l'accès Live aux vendeurs depuis l'onglet Utilisateurs (icône caméra).</p>
        </div>
        <Link to="/brocante-live" className="btn btn-primary btn-sm" data-testid="goto-live-btn">Ouvrir la Brocante Live →</Link>
      </div>
      <div className="space-y-2" data-testid="admin-live-list">
        {sessions.length === 0 && <p className="text-white/60">Aucune session pour l'instant.</p>}
        {sessions.map(s => (
          <div key={s.session_id} className="bg-white text-black p-3 flex items-center gap-3 border-2 border-white" data-testid={`admin-session-${s.session_id}`}>
            <span className={`pill shrink-0 ${s.status === "live" ? "pill-coral" : s.status === "scheduled" ? "pill-mint" : ""}`}>
              {s.status === "live" ? "EN DIRECT" : s.status === "scheduled" ? "À venir" : "Terminée"}
            </span>
            <div className="flex-1 min-w-0">
              <p className="font-bold truncate">{s.title}</p>
              <p className="text-xs text-[color:var(--color-text-muted)]">Hôte : {s.host_name} · {new Date(s.created_at).toLocaleDateString("fr-FR")}</p>
            </div>
            <div className="flex gap-2 shrink-0">
              {s.status !== "ended" && <button onClick={()=>endSession(s)} className="btn btn-sm border-2 border-black bg-white hover:bg-black hover:text-white" data-testid={`end-session-${s.session_id}`}>Terminer</button>}
              <button onClick={()=>del(s)} className="w-9 h-9 border-2 border-black flex items-center justify-center hover:bg-[color:var(--color-coral)] hover:text-white" data-testid={`del-session-${s.session_id}`}><Trash2 size={14}/></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const REASON_LABEL = {
  inapproprie: "Contenu inapproprié",
  contrefacon: "Contrefaçon",
  arnaque: "Arnaque / fraude",
  harcelement: "Harcèlement",
  spam: "Spam",
  autre: "Autre",
};

const TARGET_LABEL = { product: "Annonce", user: "Utilisateur", message: "Message", live_message: "Chat live", review: "Avis" };

function AdminReports() {
  const [reports, setReports] = useState([]);
  const [filter, setFilter] = useState("open");
  const refresh = () => api.get("/admin/reports", { params: filter !== "all" ? { status: filter } : {} }).then(r => setReports(r.data));
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [filter]);

  const setStatus = async (r, status) => {
    try {
      await api.patch(`/admin/reports/${r.report_id}`, { status });
      toast.success(status === "resolved" ? "Marqué comme traité" : "Signalement ignoré");
      refresh();
    } catch { toast.error("Erreur"); }
  };
  const delProduct = async (r) => {
    if (!window.confirm("Supprimer cette annonce signalée ?")) return;
    try {
      await api.delete(`/admin/products/${r.target_id}`);
      await api.patch(`/admin/reports/${r.report_id}`, { status: "resolved" });
      toast.success("Annonce supprimée et signalement traité");
      refresh();
    } catch { toast.error("Erreur"); }
  };

  return (
    <div data-testid="admin-reports">
      <div className="flex gap-2 mb-4">
        {[["open","À traiter"],["resolved","Traités"],["dismissed","Ignorés"],["all","Tous"]].map(([v,l]) => (
          <button key={v} onClick={()=>setFilter(v)} className={`btn btn-sm ${filter===v ? "bg-[color:var(--color-mint)] text-black border-[color:var(--color-mint)]" : "bg-white/10 text-white border-white/20"}`} data-testid={`reports-filter-${v}`}>{l}</button>
        ))}
      </div>
      <div className="space-y-2">
        {reports.length === 0 && <p className="text-white/60 text-sm py-8 text-center">Aucun signalement. 👌</p>}
        {reports.map(r => (
          <div key={r.report_id} className="bg-white text-black p-4 border-2 border-white" data-testid={`report-${r.report_id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className="pill pill-coral text-xs">{REASON_LABEL[r.reason] || r.reason}</span>
                  <span className="pill text-xs">{TARGET_LABEL[r.target_type] || r.target_type}</span>
                  {r.status !== "open" && <span className="pill text-xs bg-black text-white">{r.status === "resolved" ? "Traité" : "Ignoré"}</span>}
                </div>
                <p className="text-sm font-bold truncate">
                  {r.target_type === "product" && (r.target_info ? `« ${r.target_info.title} »` : `Annonce ${r.target_id} (supprimée ?)`)}
                  {r.target_type === "user" && (r.target_info ? `${r.target_info.name} (${r.target_info.email})` : r.target_id)}
                  {!["product","user"].includes(r.target_type) && r.target_id}
                </p>
                {r.details && <p className="text-xs text-[color:var(--color-text-muted)] mt-1">{r.details}</p>}
                <p className="label mt-1">Par {r.reporter_name || r.reporter_id} · {new Date(r.created_at).toLocaleString("fr-FR")}</p>
              </div>
              {r.status === "open" && (
                <div className="flex gap-2 shrink-0 flex-wrap">
                  {r.target_type === "product" && (
                    <>
                      <Link to={`/produit/${r.target_id}`} className="btn btn-sm border-2 border-black bg-white hover:bg-black hover:text-white" data-testid={`view-report-${r.report_id}`}>Voir</Link>
                      <button onClick={()=>delProduct(r)} className="btn btn-sm btn-coral" data-testid={`del-reported-${r.report_id}`}><Trash2 size={12}/> Supprimer</button>
                    </>
                  )}
                  {r.target_type === "user" && (
                    <Link to={`/vendeur/${r.target_id}`} className="btn btn-sm border-2 border-black bg-white hover:bg-black hover:text-white" data-testid={`view-report-${r.report_id}`}>Voir profil</Link>
                  )}
                  <button onClick={()=>setStatus(r, "resolved")} className="btn btn-sm btn-mint" data-testid={`resolve-${r.report_id}`}><Check size={12}/> Traité</button>
                  <button onClick={()=>setStatus(r, "dismissed")} className="btn btn-sm border-2 border-black bg-white hover:bg-[color:var(--color-yellow)]" data-testid={`dismiss-${r.report_id}`}>Ignorer</button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
